import React, { ReactNode, useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import { IconX } from '@tabler/icons-react';
import styles from './EnhancedModal.module.css';
import SlidingButton from '../SlidingButton/SlidingButton';
import ArrowLeft from '../../../assets/icons/ArrowLeft';
import useIsMobile from '../../../hooks/useIsMobile';
import CustomSelect from '../CustomSelect/CustomSelect';

interface TabItem {
  value: string;
  label?: string;
}

interface EnhancedModalProps {
  open: boolean;
  onClose: () => void;
  title?: string;
  subtitle?: string;
  children?: ReactNode;

  // Tab support
  tabs?: TabItem[];
  activeTab?: string;
  onTabChange?: (tabId: string) => void;

  // Footer support
  footer?: ReactNode;

  // Styling
  width?: string | number;
  height?: string | number;
  maxWidth?: string | number;
  maxHeight?: string | number;
  className?: string;
  headerClassName?: string;
  bodyClassName?: string;
  footerClassName?: string;

  // Modal behavior
  centered?: boolean;
  closeOnClickOutside?: boolean;
  closeOnEscape?: boolean;
  lockScroll?: boolean;

  // Header customization
  showHeader?: boolean;
  customHeader?: ReactNode;
  showBackButton?: boolean;
  handleBack?: () => void;
  // Close button customization
  showCloseButton?: boolean;
  customCloseButton?: ReactNode;
  onCloseButtonClick?: () => void;
  tabContainerClassName?: string;
  tabFontSize?: string;
  subTitleClassName?: string;
}

function EnhancedModal({
  open,
  onClose,
  title,
  subtitle,
  children,
  tabs,
  activeTab,
  onTabChange,
  footer,
  width = 'auto',
  height = 'auto',
  maxWidth,
  maxHeight,
  className,
  headerClassName,
  bodyClassName,
  footerClassName,
  centered = true,
  closeOnClickOutside = false,
  closeOnEscape = true,
  lockScroll = true,
  showHeader = true,
  customHeader,
  showBackButton = false,
  handleBack,
  showCloseButton = true,
  customCloseButton,
  onCloseButtonClick,
  tabContainerClassName,
  tabFontSize = '14px',
  subTitleClassName,
}: EnhancedModalProps) {
  const modalRef = useRef<HTMLDivElement>(null);
  const overlayRef = useRef<HTMLDivElement>(null);
  const isMobile = useIsMobile();

  // Handle escape key
  useEffect(() => {
    const handleEscapeKey = (event: KeyboardEvent) => {
      if (closeOnEscape && event.key === 'Escape' && open) {
        onClose();
      }
    };

    if (open) {
      document.addEventListener('keydown', handleEscapeKey);
    }

    return () => {
      document.removeEventListener('keydown', handleEscapeKey);
    };
  }, [open, closeOnEscape, onClose]);

  // Handle body scroll lock
  useEffect(() => {
    if (lockScroll && open) {
      const originalOverflow = document.body.style.overflow;
      document.body.style.overflow = 'hidden';

      return () => {
        document.body.style.overflow = originalOverflow;
      };
    }
    return undefined;
  }, [open, lockScroll]);

  // Handle focus trap
  useEffect(() => {
    if (open && modalRef.current) {
      const focusableElements = modalRef.current.querySelectorAll(
        'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])'
      );
      const firstElement = focusableElements[0] as HTMLElement;
      const lastElement = focusableElements[focusableElements.length - 1] as HTMLElement;

      const handleTabKey = (event: KeyboardEvent) => {
        if (event.key === 'Tab') {
          if (event.shiftKey) {
            if (document.activeElement === firstElement) {
              event.preventDefault();
              lastElement?.focus();
            }
          } else if (document.activeElement === lastElement) {
            event.preventDefault();
            firstElement?.focus();
          }
        }
      };

      document.addEventListener('keydown', handleTabKey);
      firstElement?.focus();

      return () => {
        document.removeEventListener('keydown', handleTabKey);
      };
    }
    return undefined;
  }, [open]);

  const handleClose = () => {
    if (onCloseButtonClick) {
      onCloseButtonClick();
    } else {
      onClose();
    }
  };

  const handleOverlayClick = (event: React.MouseEvent) => {
    if (closeOnClickOutside && event.target === overlayRef.current) {
      onClose();
    }
  };

  const handleTabClick = (tabId: string) => {
    if (onTabChange) {
      onTabChange(tabId);
    }
  };

  const renderHeader = () => {
    if (!showHeader && !customHeader) return null;

    if (customHeader) {
      return <div className={`${styles.modalHeader} ${headerClassName || ''}`}>{customHeader}</div>;
    }

    return (
      <div className={`${styles.modalHeader} ${headerClassName || ''}`}>
        {showBackButton && (
          <button className={styles.modalBackButton} onClick={handleBack} type="button">
            <ArrowLeft width={30} height={30} />
          </button>
        )}
        <div className={styles.modalHeaderContent}>
          {title && (
            <h2 id="modal-title" className={styles.modalTitle}>
              {title}
            </h2>
          )}
          {subtitle && <p className={`${styles.modalSubtitle} ${subTitleClassName || ''}`}>{subtitle}</p>}
        </div>
        {showCloseButton &&
          (customCloseButton || (
            <button className={styles.modalCloseButton} onClick={handleClose} type="button" aria-label="Close modal">
              <IconX size={16} />
            </button>
          ))}
      </div>
    );
  };

  const renderTabs = () => {
    if (!tabs || tabs.length === 0 || !activeTab) return null;

    if (isMobile) {
      return (
        <div className={styles.mobileTabsContainer}>
          <CustomSelect
            selectLabel={tabs.find((opt) => opt.value === activeTab)?.label || 'Select Status'}
            dropDownElement={(setOpened) => (
              <div className={styles.mobileDropdown}>
                {tabs.map((option) => (
                  <button
                    key={option.value}
                    type="button"
                    className={`${styles.mobileDropdownItem} ${option.value === activeTab ? styles.active : ''}`}
                    onClick={() => {
                      handleTabClick(option.value);
                      setOpened(false);
                    }}
                  >
                    {option.label}
                  </button>
                ))}
              </div>
            )}
          />
        </div>
      );
    }

    return (
      <div className={`${styles.modalTabsContainer} ${tabContainerClassName || ''}`}>
        <SlidingButton options={tabs} selected={activeTab} setSelected={handleTabClick} fontSize={tabFontSize} />
      </div>
    );
  };

  const renderBody = () => {
    return <div className={`${styles.modalBody} ${bodyClassName || ''}`}>{children}</div>;
  };

  const renderFooter = () => {
    if (!footer) return null;

    return <div className={`${styles.modalFooter} ${footerClassName || ''}`}>{footer}</div>;
  };

  const containerStyle: React.CSSProperties = {
    width,
    height,
    maxWidth,
    maxHeight,
  };

  if (!open) return null;

  const modalContent = (
    <div
      ref={overlayRef}
      className={`${styles.modalOverlay} ${centered ? styles.modalOverlayCentered : ''}`}
      onClick={handleOverlayClick}
      role="presentation"
    >
      <div
        ref={modalRef}
        className={`${styles.modalContainer} ${className || ''}`}
        style={containerStyle}
        role="dialog"
        aria-modal="true"
        aria-labelledby={title ? 'modal-title' : undefined}
        tabIndex={-1}
      >
        {renderHeader()}
        {renderTabs()}
        {renderBody()}
        {renderFooter()}
      </div>
    </div>
  );

  return createPortal(modalContent, document.body);
}

export default EnhancedModal;
